// src/pages/Transactions/TransactionsView.tsx
import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Table from '../../components/UI/Table'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
// import {Transaction}, formatNumberWithCommas, formatNumberWithCommas from '../../types'
// import {formatUSD, formatLBP, formatDate, truncateId}, formatNumberWithCommas, formatNumberWithCommas from '../../utils/helpers'
import {
    Search, Filter, ArrowUpDown, Wallet, User,
    Download, Calendar, DollarSign, TrendingUp,
    CheckCircle, XCircle, Clock, RefreshCw
} from 'lucide-react'
import {Transaction} from '../../types'
import {truncateId, formatNumberWithCommas, formatDate} from '../../utils/helpers'
import {FilterType, StatusFilter, SortOrder, DateRange, Totals} from './TransactionsModel'
// import {FilterType, StatusFilter, DateRange, SortOrder, Totals}, formatNumberWithCommas, formatNumberWithCommas from './TransactionsModel'

interface TransactionsViewProps {
    transactions: Transaction[]
    filteredTransactions: Transaction[]
    loading: boolean
    refreshing: boolean
    search: string
    filter: FilterType
    statusFilter: StatusFilter
    sortOrder: SortOrder
    dateRange: DateRange
    hasMore: boolean
    sidebarOpen: boolean
    totals: Totals
    setSearch: (value: string) => void
    setFilter: (value: FilterType) => void
    setStatusFilter: (value: StatusFilter) => void
    setSortOrder: (value: SortOrder) => void
    setDateRange: (value: DateRange) => void
    handleRefresh: () => void
    handleLoadMore: () => void
    toggleSidebar: () => void
    exportToCSV: () => void
}

export const TransactionsView: React.FC<TransactionsViewProps> = ({
    transactions,
    filteredTransactions,
    loading,
    refreshing,
    search,
    filter,
    statusFilter,
    sortOrder,
    dateRange,
    hasMore,
    sidebarOpen,
    totals,
    setSearch,
    setFilter,
    setStatusFilter,
    setSortOrder,
    setDateRange,
    handleRefresh,
    handleLoadMore,
    toggleSidebar,
    exportToCSV
}) => {
    const columns = [
        {
            key: 'id',
            title: 'المعرف',
            render: (tx: Transaction) => (
                <span className="text-sm font-medium text-gray-900">#{tx.id}</span>
            )
        },
        {
            key: 'user_id',
            title: 'المستخدم',
            render: (tx: Transaction) => (
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600" title={tx.user_id}>
                        {truncateId(tx.user_id, 10)}
                    </span>
                </div>
            )
        },
        {
            key: 'type',
            title: 'النوع',
            render: (tx: Transaction) => (
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.type === 'BUY'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                    }`}>
                    {tx.type === 'BUY' ? 'شراء' : 'بيع'}
                </span>
            )
        },
        {
            key: 'amount',
            title: 'المبلغ',
            render: (tx: Transaction) => (
                <div>
                    <p className="text-sm font-medium text-gray-900">{formatNumberWithCommas(tx.amount_usd)}</p>
                    <p className="text-xs text-gray-500">{formatNumberWithCommas(tx.amount_lbp)}</p>
                </div>
            )
        },
        {
            key: 'fee_usd',
            title: 'الرسوم',
            render: (tx: Transaction) => (
                <span className="text-sm text-gray-600">{formatNumberWithCommas(tx.fee_usd)}</span>  // ✅ صحيح
                // <span className="text-sm text-gray-600">{formatNumberWithCommas(tx.amount_usd)}</span>
            )
        },
        {
            key: 'status',
            title: 'الحالة',
            render: (tx: Transaction) => {
                const statusConfig = {
                    completed: {bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'مكتملة'},
                    pending: {bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'قيد الانتظار'},
                    failed: {bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'فشلت'}
                }
                const config = statusConfig[tx.status] || statusConfig.pending
                const Icon = config.icon

                return (
                    <span className={`px-3 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
                        <Icon className="w-3 h-3" />
                        {config.label}
                    </span>
                )
            }
        },
        {
            key: 'created_at',
            title: 'التاريخ',
            render: (tx: Transaction) => (
                <div className="text-sm text-gray-500">
                    {formatDate(tx.created_at)}
                </div>
            )
        }
    ]

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />

            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => toggleSidebar()}
                isMobile={true}
            />

            <div className="hidden lg:block">
                <Sidebar isMobile={false} />
            </div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">المعاملات</h1>
                            <p className="text-gray-500 mt-1">جميع معاملات النظام</p>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                تحديث
                            </Button>
                            <Button
                                variant="outline"
                                icon={<Download className="w-4 h-4" />}
                                onClick={exportToCSV}
                            >
                                تصدير CSV
                            </Button>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">  {/* 👈 غيرنا من 4 إلى 3 */}
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-blue-100 p-3 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المعاملات</p>
                                    <p className="text-xl font-bold text-gray-900">{totals.count}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-green-100 p-3 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المبلغ (USD)</p>
                                    <p className="text-xl font-bold text-green-600">{formatNumberWithCommas(totals.usd)}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-purple-100 p-3 rounded-lg">
                                    <Wallet className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المبلغ (LBP)</p>
                                    <p className="text-xl font-bold text-purple-600">{formatNumberWithCommas(totals.lbp)}</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-blue-100 p-3 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المعاملات</p>
                                    <p className="text-xl font-bold text-gray-900">{totals.count}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-green-100 p-3 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المبلغ (USD)</p>
                                    <p className="text-xl font-bold text-green-600">{formatNumberWithCommas(totals.usd)}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-purple-100 p-3 rounded-lg">
                                    <Wallet className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي المبلغ (LBP)</p>
                                    <p className="text-xl font-bold text-purple-600">{formatNumberWithCommas(totals.lbp)}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-orange-100 p-3 rounded-lg">
                                    <Filter className="w-5 h-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">إجمالي الرسوم</p>
                                    <p className="text-xl font-bold text-orange-600">{formatNumberWithCommas(totals.fees)}</p>
                                </div>
                            </div>
                        </Card>
                    </div> */}

                    {/* Filters */}
                    <Card className="mb-6">
                        <div className="flex flex-wrap gap-4">
                            {/* Search */}
                            <div className="flex-1 min-w-[200px] relative">
                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="بحث بمعرف المستخدم أو رقم المعاملة..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    dir="rtl"
                                />
                            </div>

                            {/* Type Filter */}
                            <div className="flex items-center gap-2">
                                <Filter className="w-5 h-5 text-gray-400" />
                                <select
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value as FilterType)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="all">جميع الأنواع</option>
                                    <option value="BUY">شراء</option>
                                    <option value="SELL">بيع</option>
                                </select>
                            </div>

                            {/* Status Filter */}
                            <div className="flex items-center gap-2">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="all">جميع الحالات</option>
                                    <option value="completed">مكتملة</option>
                                    <option value="pending">قيد الانتظار</option>
                                    <option value="failed">فشلت</option>
                                </select>
                            </div>

                            {/* Date Range Filter */}
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-gray-400" />
                                <select
                                    value={dateRange}
                                    onChange={(e) => setDateRange(e.target.value as DateRange)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="all">كل الوقت</option>
                                    <option value="today">اليوم</option>
                                    <option value="week">آخر 7 أيام</option>
                                    <option value="month">آخر 30 يوم</option>
                                </select>
                            </div>

                            {/* Sort Order */}
                            <Button
                                variant="outline"
                                icon={<ArrowUpDown className="w-4 h-4" />}
                                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                            >
                                {sortOrder === 'desc' ? 'الأحدث' : 'الأقدم'}
                            </Button>
                        </div>
                    </Card>

                    {/* Results count */}
                    <div className="mb-4 text-sm text-gray-500">
                        عرض {filteredTransactions.length} من أصل {transactions.length} معاملة
                    </div>

                    {/* Transactions Table */}
                    <Card>
                        <Table
                            columns={columns}
                            data={filteredTransactions}
                            loading={loading}
                            emptyMessage="لا توجد معاملات"
                        />

                        {hasMore && filteredTransactions.length > 0 && (
                            <div className="p-4 border-t border-gray-200 text-center">
                                <Button
                                    variant="outline"
                                    onClick={handleLoadMore}
                                    loading={loading}
                                >
                                    تحميل المزيد
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            </main>
        </div>
    )
}